import React, { useEffect, useState } from "react";
import { Sparkles, Lock, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { translateFr as tfr } from "@/i18n";

import { dateLocale } from "@/lib/utils";
export const ENT_LABELS = {
  smart_imports: "Imports intelligents & doublons",
  ai_rewrite: "Reformulations IA",
  ai_summary: tfr("Résumés IA"),
  negotiation_builder_premium: tfr("Fiche de négociation premium"),
  career_plan_full: "Career Plan Builder complet",
  career_readiness: "Career Readiness",
  career_velocity: "Career Velocity",
  pdf_versions: "Historique de versions PDF",
  qr_custom: "Destination QR configurable",
  advanced_reports: tfr("Rapports avancés"),
  agency_branding: "Branding de l'agence",
  higher_ai_allowance: tfr("Quotas IA étendus"),
  priority_support: "Support premium",
};

const fmt = (cents) => new Intl.NumberFormat(dateLocale(), { style: "currency", currency: "EUR" }).format((cents || 0) / 100);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(dateLocale(), { day: "2-digit", month: "long", year: "numeric" }); } catch (_) { return "—"; }
};

function Row({ label, children }) {
  return (
    <p className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{children}</span>
    </p>
  );
}

export default function ConfirmChangeModal({ open, onOpenChange, preview, plans, busy, onConfirm }) {
  const [ack, setAck] = useState(false);
  useEffect(() => { if (open) setAck(false); }, [open]);
  const planName = (id) => plans.find((x) => x.id === id)?.name || id;
  const isDowngrade = preview?.mode === "downgrade";
  const isUpgrade = preview?.mode === "upgrade";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="plan-confirm-modal">
        <DialogTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-sky-400" /> Confirmer le changement d'offre
        </DialogTitle>
        <DialogDescription className="sr-only">{tfr("Récapitulatif du changement d'offre avant confirmation")}</DialogDescription>
        {preview && (
          <div className="space-y-3 text-sm">
            <div className="rounded-xl border border-border p-3.5 space-y-1.5">
              <Row label={tfr("Offre actuelle")}>{planName(preview.current_plan_id)}</Row>
              <Row label={tfr("Nouvelle offre")}>{planName(preview.new_plan_id)}</Row>
              <Row label="Prise d'effet">
                {isDowngrade
                  ? `${tfr("Fin de la période en cours")}${preview.effective_date ? ` (${fmtDate(preview.effective_date)})` : ""}`
                  : preview.mode === "trial"
                    ? tfr("À l'activation de l'abonnement")
                    : tfr("Immédiate après confirmation")}
              </Row>
              {isUpgrade && (
                <Row label={tfr("Montant estimé aujourd'hui")}>
                  <span className="tabular-nums" data-testid="preview-proration">
                    {preview.proration_cents != null ? fmt(preview.proration_cents) : tfr("Calculé par Stripe")}
                  </span>
                </Row>
              )}
              <Row label={tfr("Prochaine facture complète")}>
                <span className="tabular-nums">{fmt(preview.new_amount_cents)} / mois</span>
              </Row>
              {preview.next_billing_date && !isDowngrade && (
                <Row label={tfr("Prochaine échéance")}>{fmtDate(preview.next_billing_date)}</Row>
              )}
            </div>

            {isUpgrade && (preview.gained_entitlements || []).length > 0 && (
              <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-3.5" data-testid="upgrade-gains">
                <p className="text-xs text-emerald-200/90 mb-1.5 font-medium">{tfr("Fonctionnalités débloquées :")}</p>
                <ul className="text-xs text-emerald-200/80 space-y-0.5">
                  {preview.gained_entitlements.map((e) => ENT_LABELS[e] && (
                    <li key={e} className="flex items-center gap-1.5"><Check className="h-3 w-3 shrink-0" aria-hidden />{ENT_LABELS[e]}</li>
                  ))}
                </ul>
              </div>
            )}

            {isDowngrade && (
              <>
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3.5" data-testid="downgrade-warning">
                  <p className="text-xs text-amber-200/90 mb-1.5 font-medium">
                    {tfr("Votre nouvelle offre prendra effet à la fin de la période en cours. Vos données ne seront pas supprimées, mais ces fonctionnalités deviendront indisponibles :")}
                  </p>
                  <ul className="text-xs text-amber-200/80 space-y-0.5 list-disc pl-4">
                    {(preview.lost_entitlements || []).map((e) => ENT_LABELS[e] && <li key={e}>{ENT_LABELS[e]}</li>)}
                  </ul>
                </div>
                <label className="flex items-start gap-2 text-xs text-foreground/85 cursor-pointer">
                  <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5" data-testid="downgrade-ack" />
                  {tfr("Je comprends que certaines fonctionnalités deviendront accessibles en lecture seule.")}
                </label>
              </>
            )}

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Lock className="h-3 w-3" /> Montants et proration calculés par Stripe — aucun montant n'est calculé dans le navigateur.
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="plan-modal-cancel">{tfr("Annuler")}</Button>
          <Button onClick={onConfirm} disabled={busy || (isDowngrade && !ack)} className="gap-1.5" data-testid="plan-modal-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
